import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md",
    "text-sm font-medium select-none",
    "transition-[background-color,color] duration-100 ease-out-fast",
    "focus-visible:outline-none focus-fill",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        default:
          "bg-accent text-text-inverse hover:bg-accent-hover active:bg-accent-hover",
        destructive: "bg-danger-solid text-text-inverse hover:brightness-110",
        /**
         * The neutral button: a filled well (§1), which is what tells it apart
         * from `ghost` (transparent until hovered).
         *
         * `outline` and `secondary` are deliberately THE SAME STRING.
         * `outline` was defined by its edge and `secondary` by a 4% wash; with
         * the edge gone they described one affordance separated by 0.5% alpha,
         * which is invisible in both themes. Rather than invent a third
         * neutral surface tier to preserve a distinction the design no longer
         * makes, they collapse. `secondary` is kept as an alias so the 11 call
         * sites using it keep working — prefer `outline` in new code.
         */
        outline: "bg-sunken text-text hover:bg-sunken-strong active:bg-active",
        secondary:
          "bg-sunken text-text hover:bg-sunken-strong active:bg-active",
        ghost:
          "bg-transparent text-text-secondary hover:bg-hover hover:text-text",
        link: "bg-transparent text-accent-text underline-offset-2 hover:underline",
        accent: "bg-accent-subtle text-accent-text hover:bg-accent-subtle",
      },
      size: {
        xs: "h-6 px-2 text-xs gap-1 [&_svg]:size-3.5",
        // 26px, between `xs` and the 32px control. Deliberately the geometry
        // of a board card's meta chip — same height, gutter, type and glyph —
        // so a button sitting among chips, or beside a record's title where
        // `sm` would compete with the heading, is the same size as the pills
        // it shares a row with.
        md: "h-6.5 px-2.5 text-xs gap-1.5 [&_svg]:size-3.5",
        sm: "h-control px-4",
        default: "h-control-lg px-4",
        lg: "h-9 px-6",
        icon: "size-control-lg p-0",
        "icon-sm": "size-control p-0",
        // The square counterparts of `xs` and `md`, for icon buttons that have
        // to sit level with text buttons of the same height on one row.
        "icon-xs": "size-6 p-0 [&_svg]:size-3.5",
        "icon-md": "size-6.5 p-0 [&_svg]:size-3.5",
      },
    },
    compoundVariants: [
      // `link` is text, not a control — it must not inherit a control height
      // or horizontal padding from `size`. Compound variants are emitted after
      // the size classes, so this wins for every size.
      { variant: "link", className: "h-auto p-0" },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
